// crewbit-admin/src/pages/Settings/pages/ScheduleSettingsPage.tsx
//
// Schedule Module Phase C C1 — Settings → Schedule defaults picker (admin surface).
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §8.3 + V-S1..V-S5.
// Named ScheduleSettingsPage (not SchedulePage) to avoid collision with the
// canvas page at pages/Schedule/index.tsx (which exports SchedulePage and is
// registered at /schedule, not /settings/schedule).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { lookupErrorCopy } from '../../../lib/errorMap';
import { useAuthStore } from '../../../stores/authStore';
import { useIndustryLens } from '../../../hooks/useIndustryLens';
import { useResolvedClusterConfig } from '../../../hooks/useResolvedClusterConfig';
import { useBusinessSchedulePreferences } from '../../../hooks/useBusinessSchedulePreferences';
import { useUpdateBusinessSchedulePreferences } from '../../../hooks/useUpdateBusinessSchedulePreferences';
import {
  useUpdateUserSchedulePreferences,
  type ScheduleZoomMobile,
  type ScheduleZoomDesktop,
  type ScheduleLensMobile,
  type ScheduleLensDesktop,
} from '../../../hooks/useUpdateUserSchedulePreferences';
import {
  getScheduleZoomOptions,
  getScheduleLensOptions,
  type ScheduleViewOption,
  type ScheduleZoomOptionValue,
  type ScheduleLensOptionValue,
} from '../../../utils/scheduleViewOptions';
import { palette, admin } from '../../../styles/tokens';

interface UserPreferenceState {
  mobileZoom: ScheduleZoomMobile | null;
  desktopZoom: ScheduleZoomDesktop | null;
  mobileLens: ScheduleLensMobile | null;
  desktopLens: ScheduleLensDesktop | null;
}

interface BusinessPreferenceState {
  walkInEnabled: boolean;
  leaseEndMarkersEnabled: boolean;
  dropOffWindowsEnabled: boolean;
}

export function ScheduleSettingsPage() {
  const publicUserId = useAuthStore((s): string | undefined => s.user?.id);
  const { categoryId } = useIndustryLens();
  const { config: clusterConfig } = useResolvedClusterConfig(categoryId);
  const { mutate, loading: saving } = useUpdateUserSchedulePreferences();
  const { data: businessPrefsData } = useBusinessSchedulePreferences();
  const {
    mutate: updateBusinessPrefs,
    loading: savingBusinessPrefs,
  } = useUpdateBusinessSchedulePreferences();

  const [prefs, setPrefs] = useState<UserPreferenceState>({
    mobileZoom: null,
    desktopZoom: null,
    mobileLens: null,
    desktopLens: null,
  });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [businessPrefs, setBusinessPrefs] = useState<BusinessPreferenceState>({
    walkInEnabled: false,
    leaseEndMarkersEnabled: false,
    dropOffWindowsEnabled: false,
  });

  useEffect(() => {
    if (!businessPrefsData) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mirror fetched business prefs into local editable state
    setBusinessPrefs({
      walkInEnabled: businessPrefsData.walk_in_enabled,
      leaseEndMarkersEnabled: businessPrefsData.lease_end_markers_enabled,
      dropOffWindowsEnabled: businessPrefsData.drop_off_windows_enabled,
    });
  }, [businessPrefsData]);

  const refetchPrefs = useCallback(async () => {
    if (!publicUserId) {
      setLoading(false);
      return;
    }
    // Industry Engine Phase 1c — the 5 per-user Schedule columns moved from
    // `users` to `user_preferences`; a user may have no row yet (all-null is the
    // correct inherit-from-cluster fallback), so .maybeSingle() stays.
    const { data, error } = await supabase
      .from('user_preferences')
      .select('schedule_default_zoom_mobile, schedule_default_zoom_desktop, schedule_default_lens_mobile, schedule_default_lens_desktop')
      .eq('user_id', publicUserId)
      .maybeSingle();
    if (!error && data) {
      setPrefs({
        mobileZoom: (data.schedule_default_zoom_mobile as ScheduleZoomMobile | null) ?? null,
        desktopZoom: (data.schedule_default_zoom_desktop as ScheduleZoomDesktop | null) ?? null,
        mobileLens: (data.schedule_default_lens_mobile as ScheduleLensMobile | null) ?? null,
        desktopLens: (data.schedule_default_lens_desktop as ScheduleLensDesktop | null) ?? null,
      });
    }
    setLoading(false);
  }, [publicUserId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial async fetch on mount; refetchPrefs internally setLoading(true) then setPrefs(...) — standard pattern for SELECT-on-mount, no cascading-render risk in practice
    void refetchPrefs();
  }, [refetchPrefs]);

  const onSelect = useCallback(
    async (
      field: 'mobileZoom' | 'desktopZoom' | 'mobileLens' | 'desktopLens',
      value: ScheduleZoomOptionValue | ScheduleLensOptionValue,
    ) => {
      setErrorMsg(null);
      const update =
        field === 'mobileZoom'
          ? { mobileZoom: value === 'inherit' ? null : (value as ScheduleZoomMobile) }
          : field === 'desktopZoom'
            ? { desktopZoom: value === 'inherit' ? null : (value as ScheduleZoomDesktop) }
            : field === 'mobileLens'
              ? { mobileLens: value === 'inherit' ? null : (value as ScheduleLensMobile) }
              : { desktopLens: value === 'inherit' ? null : (value as ScheduleLensDesktop) };
      const result = await mutate(update);
      if (!result.success) {
        setErrorMsg(lookupErrorCopy('update_user_schedule_preferences', result.error.code));
        return;
      }
      await refetchPrefs();
    },
    [mutate, refetchPrefs],
  );

  const desktopZoomOptions = useMemo(
    () => getScheduleZoomOptions({ surface: 'desktop', clusterConfig }),
    [clusterConfig],
  );
  const mobileZoomOptions = useMemo(
    () => getScheduleZoomOptions({ surface: 'mobile', clusterConfig }),
    [clusterConfig],
  );
  const desktopLensOptions = useMemo(
    () => getScheduleLensOptions({ surface: 'desktop', clusterConfig }),
    [clusterConfig],
  );
  const mobileLensOptions = useMemo(
    () => getScheduleLensOptions({ surface: 'mobile', clusterConfig }),
    [clusterConfig],
  );

  const desktopZoomSelected: string = prefs.desktopZoom ?? 'inherit';
  const mobileZoomSelected: string = prefs.mobileZoom ?? 'inherit';
  const desktopLensSelected: string = prefs.desktopLens ?? 'inherit';
  const mobileLensSelected: string = prefs.mobileLens ?? 'inherit';

  const onToggleBusinessPref = useCallback(async (
    key: keyof BusinessPreferenceState,
  ) => {
    setErrorMsg(null);
    const next = !businessPrefs[key];
    const patch =
      key === 'walkInEnabled'
        ? { walkInEnabled: next }
        : key === 'leaseEndMarkersEnabled'
          ? { leaseEndMarkersEnabled: next }
          : { dropOffWindowsEnabled: next };
    const result = await updateBusinessPrefs(patch);
    if (!result.success) {
      setErrorMsg(result.error ?? 'Failed to update business schedule preference.');
      return;
    }
    setBusinessPrefs((prev) => ({ ...prev, [key]: next }));
  }, [businessPrefs, updateBusinessPrefs]);

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: admin.header, marginBottom: 8 }}>
        Schedule
      </h1>
      <p style={{ fontSize: 14, color: admin.caption, marginBottom: 32 }}>
        Set how Schedule opens for you on each surface. Mobile and desktop save independently.
      </p>

      {errorMsg ? (
        <div style={{
          padding: 12, borderRadius: 8, backgroundColor: palette.redWash,
          color: palette.red, fontSize: 14, marginBottom: 16,
        }}>
          {errorMsg}
        </div>
      ) : null}

      <Section
        title="Desktop default zoom"
        subtitle="How /schedule opens on web admin"
        options={desktopZoomOptions}
        selected={desktopZoomSelected}
        onSelect={(v) => onSelect('desktopZoom', v as ScheduleZoomOptionValue)}
        loading={loading || saving}
      />

      <Section
        title="Desktop default lens"
        subtitle="Default lens for desktop schedule"
        options={desktopLensOptions}
        selected={desktopLensSelected}
        onSelect={(v) => onSelect('desktopLens', v as ScheduleLensOptionValue)}
        loading={loading || saving}
      />

      <Section
        title="Mobile default zoom"
        subtitle="How Schedule opens on your phone"
        options={mobileZoomOptions}
        selected={mobileZoomSelected}
        onSelect={(v) => onSelect('mobileZoom', v as ScheduleZoomOptionValue)}
        loading={loading || saving}
      />

      <Section
        title="Mobile default lens"
        subtitle="Default lens for mobile schedule"
        options={mobileLensOptions}
        selected={mobileLensSelected}
        onSelect={(v) => onSelect('mobileLens', v as ScheduleLensOptionValue)}
        loading={loading || saving}
      />

      <ToggleSection
        title="Business schedule features"
        subtitle="Configure schedule-level business flags."
        loading={savingBusinessPrefs}
        rows={[
          {
            key: 'walkInEnabled',
            label: 'Walk-in queue',
            description: 'Enable walk-in capture and assignment flow.',
            value: businessPrefs.walkInEnabled,
          },
          {
            key: 'leaseEndMarkersEnabled',
            label: 'Lease-end markers',
            description: 'Show lease-end boundaries in month zoom.',
            value: businessPrefs.leaseEndMarkersEnabled,
          },
          {
            key: 'dropOffWindowsEnabled',
            label: 'Drop-off windows',
            description: 'Enable drop-off window visuals and behavior.',
            value: businessPrefs.dropOffWindowsEnabled,
          },
        ]}
        onToggle={onToggleBusinessPref}
      />
    </div>
  );
}

interface SectionProps {
  title: string;
  subtitle: string;
  options: ScheduleViewOption[];
  selected: string;
  onSelect: (value: string) => void;
  loading: boolean;
}

function Section({ title, subtitle, options, selected, onSelect, loading }: SectionProps) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: admin.header }}>{title}</div>
      <div style={{ fontSize: 13, color: admin.caption, marginTop: 4, marginBottom: 16 }}>
        {subtitle}
      </div>
      <div style={{
        backgroundColor: admin.surface,
        border: `1px solid ${admin.border}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        {options.map((opt, idx) => {
          const isSelected = opt.value === selected;
          const isDisabled = !!opt.disabled;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                if (isDisabled || loading) return;
                onSelect(opt.value);
              }}
              disabled={isDisabled || loading}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '14px 18px',
                borderBottom: idx < options.length - 1 ? `1px solid ${admin.border}` : 'none',
                background: isSelected ? palette.accentWash : 'transparent',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                border: 'none',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                display: 'block',
              }}
            >
              <div style={{
                fontSize: 14,
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? palette.accent : admin.body,
              }}>
                {opt.label}
              </div>
              {opt.subtitle ? (
                <div style={{ fontSize: 12, color: admin.caption, marginTop: 2 }}>
                  {opt.subtitle}
                </div>
              ) : null}
              {isDisabled && opt.disabledReason ? (
                <div style={{ fontSize: 12, color: admin.caption, marginTop: 2, fontStyle: 'italic' }}>
                  {opt.disabledReason}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ToggleRow {
  key: keyof BusinessPreferenceState;
  label: string;
  description: string;
  value: boolean;
}

interface ToggleSectionProps {
  title: string;
  subtitle: string;
  rows: ToggleRow[];
  loading: boolean;
  onToggle: (key: keyof BusinessPreferenceState) => void;
}

function ToggleSection({ title, subtitle, rows, loading, onToggle }: ToggleSectionProps) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: admin.header }}>{title}</div>
      <div style={{ fontSize: 13, color: admin.caption, marginTop: 4, marginBottom: 16 }}>
        {subtitle}
      </div>
      <div style={{ backgroundColor: admin.surface, border: `1px solid ${admin.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {rows.map((row, idx) => (
          <button
            key={row.key}
            type="button"
            onClick={() => {
              if (loading) return;
              onToggle(row.key);
            }}
            disabled={loading}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '14px 18px',
              borderBottom: idx < rows.length - 1 ? `1px solid ${admin.border}` : 'none',
              background: 'transparent',
              cursor: loading ? 'not-allowed' : 'pointer',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: admin.body }}>{row.label}</div>
                <div style={{ fontSize: 12, color: admin.caption, marginTop: 2 }}>{row.description}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: row.value ? palette.accent : palette.g500 }}>
                {row.value ? 'ON' : 'OFF'}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default ScheduleSettingsPage;
